
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import LoanDetailsCard from "components/loan-details-card/LoanDetailsCard";
import TasksCoachmark from "components/v2/TasksCoachmark/TasksCoachmark";
import LoanOfficerCard from "components/v2/loan-officer-card/LoanOfficerCard";
import GuideLoanOfficerCard from "components/v2/guide-loan-officer-card/GuideLoanOfficerCard";
import TaskCard from "components/v2/task-card/TaskCard";
import {
  getCurrentUserId,
  getCurrentUserName,
  getDealId,
  getEncompassLoanId,
  getLoanId,
  getLoanNumber,
  getNhDealApplicationId,
  getPartnerName,
  getProspectId,
  getIsInitialUqualReferral,
  getSelfReferralInSession,
  getUserRole,
  setPartnerSession,
} from "features/authSlice";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { useUqualReferralState } from "hooks/useUqualReferralState";
import styles from "./home.module.scss";
import { LoadingPageProps } from "../loadingPageProps";
import { useCheckCreditScoreQuery, useCreateDashboardSessionMutation, useGetDashboardItemsQuery, useGetLoanStatusQuery, useUpdatePartnerSessionMutation, useGetUserStateLogicQuery, useSetStatusFlagOfUserMutation } from "pages/dashboard/home/dashboardService";
import { useInsuranceEligibility } from "pages/dashboard/hooks/useInsuranceEligibility";
import MilestoneCard from "components/v2/milestone-card/MilestoneCard";
import UqualCard from "pages/dashboard/v2/uqual-card/UqualCard";
import { useUqualFullStoryTrackingProps } from "hooks/useUqualFullStoryTrackingProps";
import NativeAppPromoCard from "components/v2/native-app-promo-card/NativeAppPromoCard";
import NpsSurvey from "pages/survey/nps";
import { useLoanOfficer } from "hooks/useLoanOfficer";
import { useNativeAppPromoVisibility } from "hooks/useNativeAppPromoVisibility";
import { DASHBOARD_SESSION_ID, DASHBOARD_SESSION_START_TIMESTAMP, SERVICING_PORTAL_URL } from "utilities/Constant";
import { RegistrationRoleType } from "pages/registration/registrationService";
import { getLoanProgress } from "pages/dashboard/store/dashboardSlice";
import ServicingCtaCard from "components/v2/servicing-cta-card/ServicingCtaCard";
import { trackFSServicingCtaClicked } from "utilities/full-story";
import RedesignAnnouncementModal from "pages/announcements/re-design/re-design-modal";
import { getUqualProgramEligibility } from "utilities/uqualProgramEligibility";

const Home: React.FC<LoadingPageProps> = ({ onLoad, isCreatingNeeds }: LoadingPageProps) => {
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const prospectId = useSelector(getProspectId);
  const loanId = useSelector(getLoanId);
  const userId = useSelector(getCurrentUserId);
  const currentUserName = useSelector(getCurrentUserName);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const [hasNpsSurveyBeenRendered, setHasNpsSurveyBeenRendered] = useState(false);
  const [showGuideCard, setShowGuideCard] = useState(false);
  const [isUserIdle, setIsUserIdle] = useState(false);
  const { loAdEligibilityFromQuery, isPromoCardVisible } = useNativeAppPromoVisibility();

  const {
    data: dashboardData,
    isLoading: isDashboardLoading
  } = useGetDashboardItemsQuery(
    { dealApplicationId: nhDealApplicationId, userId },
    { skip: !nhDealApplicationId || !userId }
  );
  const isUqualSelfReferralDone = dashboardData?.isUqualSelfReferralDone;
  const selfReferralInSession = useSelector(getSelfReferralInSession);
  const isInitialUqualReferral = useSelector(getIsInitialUqualReferral);
  const { effectiveShowHomeCard } = useUqualReferralState({
    skipQuery: !nhDealApplicationId || !userId,
  });
  const { data: userStateLogic, isLoading: isUserStateLogicLoading } = useGetUserStateLogicQuery(
    { loanId, nhDealApplicationId, userId },
    { skip: !loanId || !nhDealApplicationId || !userId }
  );
  const { data: isCreditScoreLow, isLoading: isCreditScoreLoading } = useCheckCreditScoreQuery(
    { nhDealApplicationId, prospectId },
    { skip: !nhDealApplicationId || !prospectId }
  );
  const uqualProgramEligible = getUqualProgramEligibility({
    isCreditScoreLow,
    isCreditScoreLoading,
    uQualFlagEnabled: featureFlag?.uQualSelfReferralAdLogicEnabled,
    isUqualSelfReferralDone,
    isDashboardLoading,
    selfReferralInSession,
    isInitialUqualReferral,
  });
  useInsuranceEligibility(
    nhDealApplicationId,
    userId,
    featureFlag?.insuranceAdEnabled,
    uqualProgramEligible,
  );
  const loanStatus = useGetLoanStatusQuery({ nhDealApplicationId }, { skip: nhDealApplicationId === "" });
  const taskCardRef = useRef<HTMLDivElement | null>(null);
  const coachmarkRef = useRef<HTMLDivElement | null>(null);
  const guideLoanOfficerRef = useRef<HTMLDivElement | null>(null);
  const [showTasksCoachmark, setShowTasksCoachmark] = useState(false);
  const [needsExist, setNeedsExist] = useState(false);
  const [tasksCoachmarkMessage, setTasksCoachmarkMessage] =
    useState<React.ReactNode>(null);

  const LO_ASSIGNED_TASKS_OUTSTANDING = userStateLogic === "LO_ASSIGNED_TASKS_OUTSTANDING";
  const NO_LO_BUSINESS_HOURS = userStateLogic === "NO_LO_BUSINESS_HOURS";
  const NO_LO_NOT_BUSINESS_HOURS = userStateLogic === "NO_LO_NOT_BUSINESS_HOURS";

  const userName = useSelector(getCurrentUserName);
  const firstName = userName.split(" ")[0];

  const [updateStatusFlagOfUser] = useSetStatusFlagOfUserMutation();

  const userRole = useSelector(getUserRole);
  const dealId = useSelector(getDealId);
  const loanNumber = useSelector(getLoanNumber);
  const encompassLoanId = useSelector(getEncompassLoanId);
  const userRoleNormalized = (userRole || "").toLowerCase(); // value can be  unknown, borrower, spousalcoborrower, nonspousalcoborrower

  const nativeAppTrackingProps = {
    username: currentUserName,
    prospectId,
    dealId,
    losLoanNumber: loanNumber,
    losGuid: encompassLoanId || loanId,
  };

  const uqualTrackingProps = useUqualFullStoryTrackingProps();

  useEffect(() => {
    const isSpousalCoBorrower = userRoleNormalized === RegistrationRoleType.SpousalCoBorrower.toLowerCase();
    const isNewForRole = isSpousalCoBorrower ? dashboardData?.isCoborrowerNew : dashboardData?.isNew;
    if (!isNewForRole || !nhDealApplicationId || !userId) return;
    void updateStatusFlagOfUser({ id: nhDealApplicationId, userId });
  }, [dashboardData?.isNew, dashboardData?.isCoborrowerNew, userRoleNormalized, nhDealApplicationId, userId, updateStatusFlagOfUser]);

  useEffect(() => {
    if (!showTasksCoachmark && !showGuideCard) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        showTasksCoachmark &&
        (coachmarkRef.current?.contains(target) ||
          taskCardRef.current?.contains(target))
      ) {
        return;
      }

      if (
        showGuideCard &&
        guideLoanOfficerRef.current?.contains(target)
      ) {
        return;
      }

      if (showTasksCoachmark) {
        sessionStorage.setItem("TASKS_COACHMARK_SHOWN", "true");
        setShowTasksCoachmark(false);
      }

      if (showGuideCard) {
        setShowGuideCard(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTasksCoachmark, showGuideCard]);

  const { loanOfficer, isLoading: isLoanOfficerLoading } = useLoanOfficer();
  const [isUqualSessionCreated, setIsUqualSessionCreated] = useState(false);

  const loanProgress = useSelector(getLoanProgress);
  const isLoanClosed = loanProgress?.status === "Closed";

  const dashboardSessionRef = useRef<{
    dashboardSessionId: string;
    dashboardSessionStartTimestamp: string;
    dashboardSessionEndTimestamp: null;
  } | null>(null);

  const getDashboardSession = useCallback(() => {
    if (dashboardSessionRef.current) {
      return dashboardSessionRef.current;
    }

    let dashboardSessionId = sessionStorage.getItem(DASHBOARD_SESSION_ID);
    let dashboardSessionStartTimestamp = sessionStorage.getItem(DASHBOARD_SESSION_START_TIMESTAMP);

    if (!dashboardSessionId) {
      dashboardSessionId = crypto.randomUUID();
      dashboardSessionStartTimestamp = new Date().toISOString();
      sessionStorage.setItem(DASHBOARD_SESSION_ID, dashboardSessionId);
      sessionStorage.setItem(DASHBOARD_SESSION_START_TIMESTAMP, dashboardSessionStartTimestamp);
    }

    dashboardSessionRef.current = {
      dashboardSessionId,
      dashboardSessionStartTimestamp: dashboardSessionStartTimestamp ?? new Date().toISOString(),
      dashboardSessionEndTimestamp: null,
    };

    return dashboardSessionRef.current;
  }, []);

  const [createDashboardSession] = useCreateDashboardSessionMutation();

  const partnerAdId = useSelector(getPartnerName);
  const [updatePartnerSession] = useUpdatePartnerSessionMutation();
  const [isDashboardSessionCreated, setIsDashboardSessionCreated] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    const allDataLoaded =
      !isLoanOfficerLoading &&
      !loanStatus.isLoading &&
      !isDashboardLoading &&
      !isUserStateLogicLoading &&
      !isCreditScoreLoading &&
      !isCreatingNeeds;

    if (allDataLoaded) {
      onLoad?.();
    }
  }, [isLoanOfficerLoading, loanStatus.isLoading, isDashboardLoading, isUserStateLogicLoading, isCreditScoreLoading, isCreatingNeeds, onLoad]);

  useEffect(() => {
    if (featureFlag?.npsSurveyEnabled && !isLoanOfficerLoading && !hasNpsSurveyBeenRendered) {
      setHasNpsSurveyBeenRendered(true);
    }
  }, [featureFlag?.npsSurveyEnabled, isLoanOfficerLoading, hasNpsSurveyBeenRendered]);

  useEffect(() => {
    let hasUserInteracted = false;

    const handleUserActivity = () => {
      if (!hasUserInteracted) {
        hasUserInteracted = true;
        clearTimeout(idleTimer);

        document.removeEventListener('mousedown', handleUserActivity);
        document.removeEventListener('keydown', handleUserActivity);
        document.removeEventListener('touchstart', handleUserActivity);
      }
    };

    const idleTimer = setTimeout(() => {
      if (!hasUserInteracted) {
        setIsUserIdle(true);
      }
    }, 5000);

    document.addEventListener('mousedown', handleUserActivity);
    document.addEventListener('keydown', handleUserActivity);
    document.addEventListener('touchstart', handleUserActivity);

    return () => {
      clearTimeout(idleTimer);
      document.removeEventListener('mousedown', handleUserActivity);
      document.removeEventListener('keydown', handleUserActivity);
      document.removeEventListener('touchstart', handleUserActivity);
    };
  }, []);

  const handleIdleStateGuideDisplay = () => {
    if (!featureFlag?.userGuidance) return;
    const hasAssignedLoanOfficer = Boolean(loanOfficer?.fullName?.trim());

    if (LO_ASSIGNED_TASKS_OUTSTANDING && hasAssignedLoanOfficer) {
      setShowGuideCard(false);
      if (featureFlag?.userGuidance && needsExist) {
        setTasksCoachmarkMessage(
          <>
            Now is a good time to focus on your tasks.
            If you have any questions, contact {loanOfficer?.fullName}.
          </>
        );
        setShowTasksCoachmark(true);
      }
      return;
    }

    if (hasAssignedLoanOfficer) {
      setShowGuideCard(false);
    } else {
      setShowGuideCard(true);
      if (featureFlag?.userGuidance && needsExist) {
        if (NO_LO_NOT_BUSINESS_HOURS) {
          setTasksCoachmarkMessage(
            <>
              We'll be calling you shortly to discuss your options.
              In the meantime, you can focus on completing your tasks.
            </>
          );
        } else {
          setTasksCoachmarkMessage(
            <>
              Now is a good time to focus on your tasks.
              If you have any questions, contact our team.
            </>
          );
        }
        setShowTasksCoachmark(true);
      }
    }
  };

  useEffect(() => {
    if (!isUserIdle) return;
    handleIdleStateGuideDisplay();
  }, [isUserIdle, featureFlag?.userGuidance, LO_ASSIGNED_TASKS_OUTSTANDING, NO_LO_BUSINESS_HOURS, NO_LO_NOT_BUSINESS_HOURS, needsExist, loanOfficer?.fullName]);

  useEffect(() => {
    if (!userId || !nhDealApplicationId) return;

    const sessionKey = `dashboardSession_${userId}_${nhDealApplicationId}`;
    const existingSession = sessionStorage.getItem(sessionKey);

    if (!existingSession) {
      void createDashboardSession({
        nhDealApplicationId,
        userId,
        dashboardSession: getDashboardSession(),
      });
      sessionStorage.setItem(sessionKey, "true");
    }
    setIsDashboardSessionCreated(true);

  }, [userId, nhDealApplicationId, createDashboardSession, getDashboardSession]);

  useEffect(() => {
    if (isDashboardSessionCreated && partnerAdId) {
      void updatePartnerSession({
        nhDealApplicationId,
        userId,
        partnerAdId,
      });
    }
  }, [isDashboardSessionCreated, partnerAdId, nhDealApplicationId, userId, updatePartnerSession]);

  useEffect(() => {
    const shouldRunForBorrower =
      userRoleNormalized === RegistrationRoleType.Borrower.toLowerCase() &&
      dashboardData?.isNew;

    const shouldRunForSpouse =
      userRoleNormalized === RegistrationRoleType.SpousalCoBorrower.toLowerCase() &&
      dashboardData?.isCoborrowerNew;

    if (!featureFlag || (!shouldRunForBorrower && !shouldRunForSpouse)) {
      return;
    }

    let partnerAdId: string | undefined = undefined;

    if (isCreditScoreLow && !isUqualSelfReferralDone && !isUqualSessionCreated) {
      partnerAdId = PartnerAdId[PartnerAdId.Uqual];
      setIsUqualSessionCreated(true);
    }

    if (partnerAdId) {
      dispatch(setPartnerSession(partnerAdId));
    }
  }, [
    dashboardData?.isNew,
    dashboardData?.isCoborrowerNew,
    featureFlag?.uQualSelfReferralAdLogicEnabled,
    isCreditScoreLow,
    isUqualSelfReferralDone,
    selfReferralInSession,
    isUqualSessionCreated,
    dispatch,
    userRoleNormalized,
    featureFlag
  ]);

  const shouldMountUqualCard = (): boolean => {
    if (!isCreditScoreLow) return false;
    if (!featureFlag?.uQualSelfReferralAdLogicEnabled) return false;
    return effectiveShowHomeCard || isInitialUqualReferral;
  };

  const handleServicingCtaClick = () => {
    trackFSServicingCtaClicked(userId, nhDealApplicationId);
    globalThis.open(SERVICING_PORTAL_URL, "_blank");
  };

  const isNativeAppPromoVisible =
    isPromoCardVisible &&
    loAdEligibilityFromQuery?.email === loanOfficer?.workEmail &&
    loAdEligibilityFromQuery?.isEligible;

  return (
    <>
      {hasNpsSurveyBeenRendered && <NpsSurvey />}
      <RedesignAnnouncementModal />
      {showGuideCard && (
        <button
          type="button"
          className={styles.overlay}
          onClick={() => setShowGuideCard(false)}
          aria-label="Close guide card"
        />
      )}
      {showTasksCoachmark && <div className={styles.coachmarkBackdrop} />}
      <div className={styles.bodyContent}>
        <div className={styles.taskContent}>
          <div className={styles.milestoneCardContent}>
            <MilestoneCard />
          </div>
          {isLoanClosed && (
            <ServicingCtaCard onClick={handleServicingCtaClick} />
          )}
          <LoanDetailsCard />

          <div
            ref={taskCardRef}
            className={
              showTasksCoachmark
                ? styles.taskHighlightWrapper
                : styles.taskContent
            }
          >
            <TaskCard onNeedsLoaded={setNeedsExist} />

            {showTasksCoachmark && (
              <div ref={coachmarkRef}>
                <TasksCoachmark
                  targetRef={taskCardRef}
                  firstName={firstName}
                  message={tasksCoachmarkMessage}
                  onClose={() => {
                    setShowTasksCoachmark(false);
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <div className={styles.adContent}>
          <div className={styles.loanOfficerDetails}>
            <div className={showGuideCard ? styles.guideHighlightWrapper : styles.loanOfficerWrapper}>
              {showGuideCard && (
                <div ref={guideLoanOfficerRef}>
                  <GuideLoanOfficerCard
                    loInfo={loanOfficer}
                    firstName={firstName}
                    onClose={() => setShowGuideCard(false)}
                  />
                </div>
              )}

              <LoanOfficerCard loInfo={loanOfficer} />
            </div>

          </div>
          {shouldMountUqualCard() && (
            <div className={styles.adInfo}>
              <span className={styles.adName}>NAF Recommendations</span>
              <UqualCard trackingProps={uqualTrackingProps} />
            </div>
          )}
          {isNativeAppPromoVisible && (
            <div className={styles.adInfo}>
              <NativeAppPromoCard trackingProps={nativeAppTrackingProps} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Home;

export enum PartnerAdId {
  None = 0,
  NafInsuranceMatic = 1,
  Uqual = 2,
}

